import React from "react";
import PropTypes from "prop-types";
import { Card } from "react-md";
import { getInspectorPinURL } from "components/Shared/Map";
import { AppInsightsTracker } from "components/Shared";
import { getSearchParameter } from "components/Shared/Functions";
import FieldRepProfileV2 from "components/Homeowner/FieldReps/FieldRepProfileV2";
import MSplitter from "components/Shared/MSplitter.tsx";
import FieldRepCreate from "components/Homeowner/FieldReps/FieldRepCreate";
import { createFieldRep } from "requests/homeowner/fieldReps";
import MGrid, {
  COL_CODE_FULL_NAME,
  COL_CODE_FR_NUMBER,
  COL_CODE_PAYROLL_EMPLOYEE_ID,
  COL_CODE_ADDRESS,
  COL_CODE_CITY,
  COL_CODE_STATE,
  COL_CODE_ZIP,
  COL_CODE_COUNTY,
  COL_CODE_EMAIL,
  COL_CODE_IS_ACTIVE,
  COL_CODE_LATITUDE,
  COL_CODE_LONGITUDE,
  ACTION_CONFIGURE,
  ACTION_MAP,
  ACTION_ADD,
  FILTER_TYPE_MULTI_SELECT,
  FILTER_TYPE_NONE
} from "components/Shared/MGrid.tsx";
import { DATA_TYPE_DATE, DATA_TYPE_BOOLEAN } from "typeConstants/dataTypes";
import * as employeeRoles from "typeConstants/employeeRoles";
import { getEmployeesByRoles } from "requests/employee";

const gridCols = [
  { code: COL_CODE_FR_NUMBER, dataSource: "number" },
  { code: COL_CODE_PAYROLL_EMPLOYEE_ID, dataSource: "payrollEmployeeId" },
  { code: COL_CODE_FULL_NAME, dataSource: "fullName" },
  { code: COL_CODE_ADDRESS, dataSource: "address" },
  { code: COL_CODE_CITY, dataSource: "city" },
  { code: COL_CODE_STATE, dataSource: "state" },
  { code: COL_CODE_ZIP, dataSource: "zip" },
  { code: COL_CODE_COUNTY, dataSource: "county" },
  { code: COL_CODE_EMAIL, dataSource: "email" },
  { key: "isInTraining", name: "Trainee", dataType: DATA_TYPE_BOOLEAN, dataSource: "isInTraining", filterType: FILTER_TYPE_MULTI_SELECT, width: 80 },
  { key: "frManager", name: "Manager", width: 160, dataSource: "manager", filterType: FILTER_TYPE_MULTI_SELECT },
  { code: COL_CODE_IS_ACTIVE, dataSource: "isActive", visible: false },
  { code: COL_CODE_LATITUDE, dataSource: "latitude", visible: false },
  { code: COL_CODE_LONGITUDE, dataSource: "longitude", visible: false },
  { key: "trainingCompletedDateFieldRep", name: "Training Completed On", dataType: DATA_TYPE_DATE, dataSource: "trainingCompletedDate", visible: true, filterType: FILTER_TYPE_NONE, width: 180 },
  { key: "fieldDevelopmentManager", name: "Field Development Mgr", dataSource: "fieldDevelopmentManager", width: 180, filterType: FILTER_TYPE_MULTI_SELECT }
];

class FieldRepList extends AppInsightsTracker {
  constructor(props) {
    super(props);

    this.getMapPinForRow = this.getMapPinForRow.bind(this);
    this.getMapToolTipForRow = this.getMapToolTipForRow.bind(this);
    this.onRowClick = this.onRowClick.bind(this);
    this.navigateToFieldRep = this.navigateToFieldRep.bind(this);
    this.onCreate = this.onCreate.bind(this);
    this.handleFormChange = this.handleFormChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSubmitCreateAnother = this.onSubmitCreateAnother.bind(this);
    this.hideCreateDialog = this.hideCreateDialog.bind(this);
    this.viewNewFieldRep = this.viewNewFieldRep.bind(this);
    this.createAnotherFieldRep = this.createAnotherFieldRep.bind(this);

    this.mounted = { current: true };

    this.state = {
      frNumber: null,
      internetUserId: null,
      newFieldRep: {},
      createFieldRepDialogVisible: false,
      edited: false,
      fieldLevelCoordinators: [],
      fieldDevelopmentManagers: [],
      creatingFieldRep: false,
    };
  }

  componentDidMount() {
    const { getFieldRepList, stateList, getStateList, allTeamLeaders, getAllTeamLeaders, canCreate } = this.props;
    getFieldRepList(getSearchParameter(this.props.location));

    if (canCreate) {
      if (!stateList || stateList.length === 0) getStateList();
      if (!allTeamLeaders || allTeamLeaders.length === 0) getAllTeamLeaders();
      if (!this.state.fieldLevelCoordinators || this.state.fieldLevelCoordinators.length === 0) this.getFieldLevelCoordinators();
      if (!this.state.fieldDevelopmentManagers || this.state.fieldDevelopmentManagers.length === 0) this.getFieldDevelopmentManagers();
    }
  }

  getFieldLevelCoordinators() {
    getEmployeesByRoles([employeeRoles.FIELD_OPERATIONS_MANAGER], data => this.setState({ fieldLevelCoordinators: data }), this.mounted);
  }

  getFieldDevelopmentManagers() {
    getEmployeesByRoles([employeeRoles.FIELD_DEVELOPMENT_MANAGER], data => this.setState({fieldDevelopmentManagers: data}), this.mounted);
  }

  componentWillUnmount() {
    this.mounted.current = false;
  }

  getMapPinForRow(rowIndex) {
    const row = this.props.fieldReps[rowIndex];
    return {
      latitude: row.latitude,
      longitude: row.longitude,
      iconURL: getInspectorPinURL(false, false)
    };
  }

  getMapToolTipForRow(rowIndex) {
    const row = this.props.fieldReps[rowIndex];
    return {
      title: `[${row.number}] ${row.fullName}`,
      fields: [
        { label: "Address", value: `${row.address} ${row.city}` },
        { label: "Email", value: row.email }
      ]
    };
  }

  onRowClick(rowIndex) {
    const row = this.props.fieldReps[rowIndex];

    this.setState({ frNumber: row.number, internetUserId: row.internetUserId });
  }

  navigateToFieldRep() {
    let pathname = "/field-rep-profile";
    this.props.history.push({
      pathname: pathname,
      state: {
        frNumber: this.state.frNumber,
        internetUserId: this.state.internetUserId
      }
    });
  }

  onCreate() {
    this.setState({ createFieldRepDialogVisible: true });
  }

  handleFormChange(updatedObject) {
    this.setState({ newFieldRep: { ...this.state.newFieldRep, ...updatedObject }, edited: true });
  }

  onSubmit() {
    this.setState({ creatingFieldRep: true });
    createFieldRep(this.state.newFieldRep, this.viewNewFieldRep, () => this.setState({ creatingFieldRep: false }), this.mounted);
  }

  onSubmitCreateAnother() {
    let name = `${this.state.newFieldRep.firstName} ${this.state.newFieldRep.lastName}`;
    createFieldRep(this.state.newFieldRep, response => this.createAnotherFieldRep(name, response), () => this.setState({ creatingFieldRep: false }), this.mounted);
    this.setState({ creatingFieldRep: true });
  }

  viewNewFieldRep(response) {
    this.hideCreateDialog();
    this.setState({ frNumber: response.inspectorNumber, internetUserId: response.internetUserId });
    this.navigateToFieldRep();
  }

  createAnotherFieldRep(name, response) {
    this.props.makeToast(`${name} (${response.inspectorNumber}) created successfully`);
    this.setState({ creatingFieldRep: false, newFieldRep: {} });
  }

  hideCreateDialog() {
    this.setState({ ...this.state, createFieldRepDialogVisible: false });
  }

  render() {
    const { fieldReps, isFetchingFieldReps, stateList, allTeamLeaders, canView, canCreate } = this.props;
    const actions = [ACTION_CONFIGURE, ACTION_MAP];
    if (canCreate) actions.push({ code: ACTION_ADD, onClick: this.onCreate });
    return (
      <>
        {this.state.createFieldRepDialogVisible &&
          <FieldRepCreate
            newFieldRep={this.state.newFieldRep}
            handleFormChange={this.handleFormChange}
            teamLeaders={allTeamLeaders}
            states={stateList}
            onSubmit={this.onSubmit}
            onSubmitCreateAnother={this.onSubmitCreateAnother}
            hideCreateDialog={this.hideCreateDialog}
            fieldLevelCoordinators={this.state.fieldLevelCoordinators}
            fieldDevelopmentManagers={this.state.fieldDevelopmentManagers}
            creatingFieldRep={this.state.creatingFieldRep}
          />}
        <MSplitter
          firstChild={
            <div className="padding-page-content full-height">
              <Card className="full-height">
                <MGrid
                  configurationId="FieldRepList"
                  headerText="Active Field Reps"
                  columnDefinitions={gridCols}
                  data={fieldReps}
                  isFetchingData={isFetchingFieldReps}
                  actions={actions}
                  onRowClick={canView && this.onRowClick}
                  displayNumOfItemsFound={true}
                  getMapPinForRow={this.getMapPinForRow}
                  getMapToolTipForRow={this.getMapToolTipForRow}
                />
              </Card>
            </div>}
          secondChild={
            this.state.frNumber &&
            <FieldRepProfileV2
              frNumberOverride={this.state.frNumber}
              internetUserIdOverride={this.state.internetUserId}
              verticalDisplay
            />}
          secondChildTitle={{
            title: "Field Rep Profile",
            buttons: [
              { icon: "fullscreen", onClick: this.navigateToFieldRep },
              { icon: "close", onClick: () => this.setState({ frNumber: null }) }
            ]
          }}
        />
      </>
    );
  }
}

FieldRepList.propTypes = {
  getFieldRepList: PropTypes.func.isRequired,
  isFetchingFieldReps: PropTypes.bool.isRequired,
  fieldReps: PropTypes.array,
  history: PropTypes.object.isRequired,
  location: PropTypes.object,
  userProfile: PropTypes.object,
  getStateList: PropTypes.func,
  getAllTeamLeaders: PropTypes.func,
  stateList: PropTypes.array,               // State list for FieldRepCreate
  allTeamLeaders: PropTypes.array,          // Team leaders for FieldRepCreate
  canView: PropTypes.bool.isRequired,       // User has FieldRepProfile View right
  canCreate: PropTypes.bool.isRequired,     // User has Field Rep Create right
  makeToast: PropTypes.func,
};
export default FieldRepList;
